import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, Subject, catchError, map, mergeMap, of, tap, throwError, toArray } from 'rxjs';

import { AppConfig } from '../app.config';
import { ComponentFeatureTypeMap, ComponentRelatedTask, FeatureComponentTypeMap, FeatureWithMetadata, TaskNetworkComponent, TaskNetworkComponentBatchChange, TaskNetworkComponentChange, TaskNetworkComponentChangeBase, TaskNetworkComponentConfig, TaskNetworkComponentPatchConfig } from '../tasks/task/models/network-component.model';

@Injectable({
    providedIn: 'root'
})
export class TaskNetworkComponentService {

    private taskApiBaseUrl: string;
    private mapApiBaseUrl: string;

    private maxConcurrency: number = 2;

    private changeSubject: Subject<TaskNetworkComponentChangeBase> = new Subject();

    /**
     * Task network component changes observable.
     * 
     * Triggers when new components are added or existing is modified or deleted.
     */
    public get changes$(): Observable<TaskNetworkComponentChangeBase> {
        return this.changeSubject.asObservable();
    }

    constructor(
        private http: HttpClient,
        private config: AppConfig) {

        this.taskApiBaseUrl = this.config.backendIwmUri + 'Api/';
        this.mapApiBaseUrl = this.config.backendMapUri + 'CommonMapWebAPI/';
    }

    /**
     * Maps the given feature type to corresponding component type.
     * 
     * If map was not found `Unknown` component type is returned.
     * 
     * @param featureType Feature type to map
     * @returns Corresponding component type
     */
    public mapFeatureTypeToComponentType(featureType: string): string {
        return FeatureComponentTypeMap[featureType] ?? 'Unknown';
    }

    /**
     * Maps the given component type to corresponding feature type.
     * 
     * If map was not found `unknown` feature type is returned.
     * 
     * @param componentType Component type to map
     * @returns Corresponding feature type
     */
    public mapComponentTypeToFeatureType(componentType: string): string {
        return ComponentFeatureTypeMap[componentType] ?? 'unknown';
    }

    /**
     * Gets visible task network components.
     * 
     * @param taskId Task id to filter the components. If not provided gets all visible components.
     * @returns Visible task network components
     */
    public getComponents(taskId?: string): Observable<TaskNetworkComponent[]> {
        let url = this.taskApiBaseUrl + 'TaskNetworkComponents';

        if (taskId) {
            url += `?$filter=TaskId eq ${taskId}`;
        }

        return this.http.get<any>(url)
            .pipe(
                map(result => result.value as TaskNetworkComponent[])
            );
    }

    /**
     * Gets a single task network component.
     * 
     * @param id Component entity id
     * @returns Task network component
     */
    public getComponent(id: string): Observable<TaskNetworkComponent> {
        const url = this.taskApiBaseUrl + `TaskNetworkComponents(${id})`;

        return this.http.get<TaskNetworkComponent>(url);
    }

    /**
     * Gets visible task network component count.
     * 
     * @param taskId Task id to filter the components. If not provided gets count for all visible components.
     * @returns Visible task network component count
     */
    public getComponentCount(taskId?: string): Observable<number> {
        let url = this.taskApiBaseUrl + 'TaskNetworkComponents/$count';

        if (taskId) {
            url += `?$filter=TaskId eq ${taskId}`;
        }

        return this.http.get<string>(url)
            .pipe(
                map(result => +result)
            );
    }

    /**
     * Adds a network component to task.
     * 
     * @param config Component configuration
     * @returns Created task network component entity
     */
    public addComponent(config: TaskNetworkComponentConfig): Observable<TaskNetworkComponent> {
        const url = this.taskApiBaseUrl + 'TaskNetworkComponents';

        return this.http.post<TaskNetworkComponent>(url, config)
            .pipe(
                catchError(this.catchAlreadyAdded),
                tap(result => {
                    // De-reference the result so original subscription cannot modify it
                    const component = { ...result };

                    // Trigger change in future macrotask to allow original subscription to execute before it
                    setTimeout(() => {
                        this.changeSubject.next({
                            operation: 'add',
                            id: component.Id,
                            component
                        } as TaskNetworkComponentChange);
                    });
                })
            );
    }

    /**
     * Modifies an existing network component.
     * 
     * @param id Component entity id
     * @param config Component patch configuration
     * @returns Modified task network component entity
     */
    public editComponent(id: string, config: TaskNetworkComponentPatchConfig): Observable<TaskNetworkComponent> {
        const url = this.taskApiBaseUrl + `TaskNetworkComponents(${id})`;

        return this.http.patch<TaskNetworkComponent>(url, config)
            .pipe(
                tap(result => {
                    const component = { ...result };

                    setTimeout(() => {
                        this.changeSubject.next({
                            operation: 'edit',
                            id: component.Id,
                            component
                        } as TaskNetworkComponentChange);
                    });
                })
            );
    }

    /**
     * Deletes a network component from task.
     * 
     * @param id Component entity id
     */
    public deleteComponent(id: string): Observable<void> {
        const url = this.taskApiBaseUrl + `TaskNetworkComponents(${id})`;

        return this.http.delete<void>(url)
            .pipe(
                tap(() => {
                    setTimeout(() => {
                        this.changeSubject.next({
                            operation: 'delete',
                            id
                        } as TaskNetworkComponentChange);
                    });
                })
            );
    }

    /**
     * Adds multiple network components to task.
     * 
     * @param configs Component configurations
     * @returns Created task network component entities
     */
    public addComponentBatch(configs: TaskNetworkComponentConfig[]): Observable<TaskNetworkComponent[]> {
        const url = this.taskApiBaseUrl + 'TaskNetworkComponents';

        // Batch requests according to max concurrency & once all finished collect them into a single emission
        return of(...configs)
            .pipe(
                mergeMap(config =>
                    this.http.post<TaskNetworkComponent>(url, config)
                        .pipe(
                            catchError(this.catchAlreadyAdded)
                        ),
                    this.maxConcurrency),
                toArray(),
                tap(results => {
                    if (results.length === 0) {
                        return;
                    }

                    const components = structuredClone(results);

                    setTimeout(() => {
                        this.changeSubject.next({
                            operation: 'batchAdd',
                            batch: components.map(c => ({
                                id: c.Id,
                                component: c
                            }))
                        } as TaskNetworkComponentBatchChange);
                    });
                })
            );
    }

    /**
     * Deletes multiple network components from task.
     * 
     * @param ids Component entity ids
     */
    public deleteComponentBatch(ids: string[]): Observable<void> {
        // De-reference the array
        ids = [...ids];

        return of(...ids)
            .pipe(
                mergeMap(id => this.http.delete<void>(this.taskApiBaseUrl + `TaskNetworkComponents(${id})`), this.maxConcurrency),
                toArray(),
                map(() => undefined),
                tap(() => {
                    setTimeout(() => {
                        this.changeSubject.next({
                            operation: 'batchDelete',
                            batch: ids.map(id => ({ id }))
                        } as TaskNetworkComponentBatchChange);
                    });
                })
            );
    }

    /**
     * Searches features from Map API by text.
     * 
     * @param search Search text
     * @returns Features filtered by given text
     */
    public getFeaturesByText(search: string): Observable<FeatureWithMetadata[]> {
        const url = this.mapApiBaseUrl + 'api/Wfs/GetFeaturesWithMetadataByText';

        return this.http.get<any>(url, {
            // Using params for url-encoding
            params: {
                search
            }
        })
            .pipe(
                map(result => result.features as FeatureWithMetadata[]),
                map(results => results.map(this.trimFeature))
            );
    }

    /**
     * Gets visible tasks related to given network component.
     * 
     * @param componentId Network component id to get the related tasks
     * @returns Visible tasks related to given network component
     */
    public getComponentRelatedTasks(componentId: string): Observable<ComponentRelatedTask[]> {
        const url = this.taskApiBaseUrl + `TaskNetworkComponents(${componentId})/GetRelatedTasks`;

        return this.http.get<any>(url)
            .pipe(
                map(result => result.value as ComponentRelatedTask[])
            );
    }

    private trimFeature(feature: FeatureWithMetadata): FeatureWithMetadata {
        if (feature.properties.Label) {
            feature.properties.Label = feature.properties.Label
                .split('\\r\\n').filter(s => s).join(' ')
                .split('\\n').filter(s => s).join(' ');
        }

        return feature;
    }

    private catchAlreadyAdded(error: any): Observable<never> {
        // If component is alread added to task ignore it and complete the observable
        if (error instanceof HttpErrorResponse &&
            error.status === 400 &&
            error.error?.error?.innererror?.message?.includes('ComponentFeatureId can only be added once')) {
            return EMPTY;
        }

        return throwError(() => error);
    }
}
